import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  IconButton,
  Button,
  Typography,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { getPayments, transferPayment } from "../../State/Payment/Action";
import { FileDownload, PictureAsPdf, Print } from "@mui/icons-material";
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from "dayjs";
import { getProducts } from "../../State/Product/Action";
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { PermissionService } from "../../../config/permissionService";
import { getSelectedExportRows } from "../../../config/exportSelection";

const Payments = () => {
  const dispatch = useDispatch();
  const { payments, adminProduct } = useSelector((store) => store);

  const [openTransferModal, setOpenTransferModal] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [orderFromDate, setOrderFromDate] = useState("");
  const [orderToDate, setOrderToDate] = useState("");
  const [paymentFromDate, setPaymentFromDate] = useState("");
  const [paymentToDate, setPaymentToDate] = useState("");
  const [productTitle, setProductTitle] = useState("");
  // Add this state for selection
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  useEffect(() => {
    dispatch(getPayments());
    dispatch(getProducts());
  }, [dispatch]);

  const handleResetOrderDates = () => {
    setOrderFromDate("");
    setOrderToDate("");
  };

  const handleResetPaymentDates = () => {
    setPaymentFromDate("");
    setPaymentToDate("");
  };

  const handleTransferAmount = (paymentId) => {
    setSelectedPaymentId(paymentId);
    setOpenTransferModal(true);
  };

  const confirmTransferAmount = () => {
    if (selectedPaymentId) {
      dispatch(transferPayment(selectedPaymentId));
      setOpenTransferModal(false);
      setSelectedPaymentId(null);
    }
  };

  const getStatusChip = (status, type) => {
    let colorMap = {};
    if (type === "orderStatus") {
      colorMap = {
        "PENDING": "warning",
        "PLACED": "default",
        "SHIPPED": "info",
        "DELIVERED": "success",
        "CANCELLED": "error"
      };
    } else if (type === "paymentStatus") {
      colorMap = {
        "SUCCESS": "success",
        "FAILED": "error",
      };
    } else if (type === "transferStatus") {
      colorMap = {
        "TRANSFERRED": "success",
        "PENDING": "warning",
        "CANCELLED": "error"
      };
    }
    return <Chip label={status} color={colorMap[status] || "default"} sx={{ fontSize: "0.7rem", height: "24px", borderRadius: "12px", fontWeight: "bold" }} />;
  };

  const paymentRows =
    payments?.payments?.response?.map((payment, index) => ({
      id: payment.id,
      paymentId: payment.paymentId,
      orderId: payment.order.id,
      uniqueOrderId: payment.order.orderId,
      paymentDate: payment.paymentDate || "----",
      transferDate: payment.transferDate,
      orderDate: payment.order?.orderDate || "----",
      vendorNames:
        payment.order?.orderItems
          ?.map((item) => item.product?.vendor?.name)
          .join(" , ") || "----",
      vendorEmails:
        payment.order?.orderItems
          ?.map((item) => item.product?.vendor?.email)
          .join(" , ") || "----",
      userName: payment.order?.user?.name || "----",
      userEmail: payment.order?.user?.email || "----",
      employeeName: payment.order?.employee?.name || "----",
      employeeEmail: payment.order?.employee?.email || "----",
      productTitles:
        payment.order?.orderItems
          ?.map((item) => item.product?.title)
          .join(" , ") || "----",
      totalQuantity: payment.order.totalItem || 0,
      totalPrice: payment.order?.orderItems?.reduce(
        (sum, item) => sum + ((item?.variant?.vendorPrice || 0.00) * (item?.quantity || 0)),
        0
      ).toFixed(2),
      totalAdminPrice: payment.order?.orderTotalPrice?.toFixed(2) || 0,
      orderStatus: payment.order?.orderStatus || "----",
      paymentStatus: payment?.paymentStatus || "----",
      transferStatus: payment?.adminPaymentStatus || "PENDING",
      deliveryHistory: payment.order?.deliveryDateHistories || [],
      fullOrder: payment.order,
    })) || [];

  const filteredRows = paymentRows.filter((row) => {
    const query = searchText.toLowerCase();

    const matchesSearchText = (
      row.paymentId.toLowerCase().includes(query) ||
      row.uniqueOrderId.toLowerCase().includes(query) ||
      row.vendorNames.toLowerCase().includes(query) ||
      row.vendorEmails.toLowerCase().includes(query) ||
      row.userName.toLowerCase().includes(query) ||
      row.userEmail.toLowerCase().includes(query) ||
      row.employeeName.toLowerCase().includes(query) ||
      row.employeeEmail.toLowerCase().includes(query) ||
      row.productTitles.toLowerCase().includes(query) ||
      String(row.totalQuantity).toLowerCase().includes(query) ||
      String(row.totalPrice).toLowerCase().includes(query) ||
      String(row.totalAdminPrice).toLowerCase().includes(query) ||
      row.orderStatus.toLowerCase().includes(query) ||
      row.paymentStatus.toLowerCase().includes(query) ||
      row.transferStatus.toLowerCase().includes(query)
    );

    const matchesOrderDate =
      (!orderFromDate || dayjs(row.orderDate).isAfter(dayjs(orderFromDate).subtract(1, 'day')) || dayjs(row.orderDate).isSame(orderFromDate, 'day')) &&
      (!orderToDate || dayjs(row.orderDate).isBefore(dayjs(orderToDate).add(1, 'day')) || dayjs(row.orderDate).isSame(orderToDate, 'day'));

    const matchesPaymentDate =
      (!paymentFromDate || dayjs(row.paymentDate).isAfter(dayjs(paymentFromDate).subtract(1, 'day')) || dayjs(row.paymentDate).isSame(paymentFromDate, 'day')) &&
      (!paymentToDate || dayjs(row.paymentDate).isBefore(dayjs(paymentToDate).add(1, 'day')) || dayjs(row.paymentDate).isSame(paymentToDate, 'day'));

    const isProductTitleMatch = productTitle
      ? row.productTitles.toLowerCase().trim() === productTitle.toLowerCase().trim()
      : true;

    return matchesSearchText && matchesOrderDate && matchesPaymentDate && isProductTitleMatch;
  });
  const getExportData = () => getSelectedExportRows(filteredRows, selectedRowIds);


  // Export, PDF, and Print Handlers
  const handleExportCSV = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const csvContent = exportRows.map((row, index) => {
      return [
        index + 1,
        row.paymentId,
        row.uniqueOrderId,
        dayjs(row.paymentDate).format("DD MMM YYYY"),
        dayjs(row.orderDate).format("DD MMM YYYY"),
        row.vendorNames,
        row.vendorEmails,
        row.userName,
        row.userEmail,
        row.employeeName,
        row.employeeEmail,
        row.productTitles,
        row.totalQuantity,
        row.totalPrice,
        row.totalAdminPrice,
        row.orderStatus,
        row.transferStatus,
      ].join(',');
    }).join('\n');

    const headers = [
      'S.No.',
      'Payment ID',
      'Order ID',
      'Payment Date',
      'Order Date',
      'Vendor Names',
      'Vendor Emails',
      'Customer Name',
      'Customer Email',
      'Employee Name',
      'Employee Email',
      'Product Titles',
      'Total Quantity',
      'Total Vendor Price',
      'Total Admin Price',
      'Order Status',
      'Transfer Status',
    ].join(',');

    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'payments.csv';
    link.click();
  };

  const handleExportPDF = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a2", // even larger than A3 to fit all columns
    });

    doc.setFontSize(18);
    doc.text("Payment List", 40, 30);

    const tableColumn = [
      'S.No.',
      'Payment ID',
      'Order ID',
      'Payment Date',
      'Order Date',
      'Vendor Names',
      'Vendor Emails',
      'Customer Name',
      'Customer Email',
      'Employee Name',
      'Employee Email',
      'Product Titles',
      'Total Quantity',
      'Total Vendor Price',
      'Total Admin Price',
      'Order Status',
      'Transfer Status',
    ];

    const tableRows = exportRows.map((row, index) => ([
      index + 1,
      row.paymentId,
      row.uniqueOrderId,
      dayjs(row.paymentDate).format("DD MMM YYYY"),
      dayjs(row.orderDate).format("DD MMM YYYY"),
      row.vendorNames,
      row.vendorEmails,
      row.userName,
      row.userEmail,
      row.employeeName,
      row.employeeEmail,
      row.productTitles,
      row.totalQuantity,
      row.totalPrice,
      row.totalAdminPrice,
      row.orderStatus,
      row.transferStatus,
    ]));

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 50,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak',
        valign: 'top',
      },
      columnStyles: {
        1: { cellWidth: 100 },   // Shipping Address
        2: { cellWidth: 100 },  // Product Titles
        11: { cellWidth: 100 },  // Product Names
      },
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      bodyStyles: {
        halign: 'left'
      },
      margin: { top: 50, left: 10, right: 10, bottom: 20 },
      didDrawPage: (data) => {
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 10, doc.internal.pageSize.height - 10);
      },
    });


    doc.save('payments.pdf');
  };

  const writeAction = PermissionService.has({ category: "Admin", page: "Payments", type: "WRITE" });

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "paymentId", headerName: "Payment ID", width: 100 },
    { field: "uniqueOrderId", headerName: "Order ID", width: 100 },
    { field: "paymentDate", headerName: "Payment Date", width: 120, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----" },
    { field: "transferDate", headerName: "Transfer Date", width: 120, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----" },
    { field: "orderDate", headerName: "Order Date", width: 120, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----" },
    { field: "vendorNames", headerName: "Vendor Names", width: 100 },
    { field: "vendorEmails", headerName: "Vendor Emails", width: 100 },
    { field: "userName", headerName: "Customer Name", width: 100 },
    { field: "userEmail", headerName: "Customer Email", width: 100 },
    { field: "employeeName", headerName: "Employee Name", width: 100 },
    { field: "employeeEmail", headerName: "Employee Email", width: 100 },
    { field: "productTitles", headerName: "Product Titles", width: 120 },
    { field: "totalPrice", headerName: "Vendor Total Price", width: 100 },
    { field: "totalAdminPrice", headerName: "Customer Total Price", width: 100 },
    {
      field: "orderStatus",
      headerName: "Order Status",
      width: 120,
      renderCell: (params) => getStatusChip(params.value, "orderStatus")
    },
    {
      field: "transferStatus",
      headerName: "Vendor Transfer Status",
      width: 130,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          {getStatusChip(params.value === "TRANSFER" ? "TRANSFERRED" : params.value, "transferStatus")}
          {params.row.transferStatus !== "TRANSFER" && params.row.orderStatus === "DELIVERED" && writeAction && (
            <Tooltip title="Transfer Amount">
              <IconButton
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer", borderRadius: "50%", border: "1px solid #ef4444" }}
                onClick={() => handleTransferAmount(params.row.id)}
              >
                <CompareArrowsIcon
                  fontSize="small"
                  sx={{ fontSize: 15, cursor: "pointer", color: "#ef4444" }}
                />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    },
  ];


  const excludedFields = ['serialNo']; // Add more fields if needed

  const exportableColumns = columns.filter(col => !excludedFields.includes(col.field));
  // Get headers from column definitions
  const headers = exportableColumns.map(col => col.headerName);
  const csvFields = exportableColumns.map(col => col.field);

  const handlePrint = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const printWindow = window.open('', '_blank');
    const data = exportRows.map(row =>
      csvFields.map(field => row[field] ?? "----")
    );

    const html = `
      <html>
        <head>
          <title>Payment List</title>
          <style>
            @media print {
              @page { size: A3 landscape; }
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
            }
            table {
              border-collapse: collapse;
              width: 100%;
              table-layout: auto;
            }
            th, td {
              border: 1px solid #999;
              padding: 4px;
              text-align: left;
              white-space: pre-wrap;
              word-break: break-word;
            }
            th {
              background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            tr:nth-child(even) {
              background: #f5f5f5;
            }
          </style>
        </head>
        <body>
          <h2>Payment List</h2>
          <table>
            <thead>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${data.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 300);
    };
  };
  return (
    <div>
      <Box p={2} bgcolor="white" borderRadius={2} boxShadow={1} marginBottom={2}>
        {/* Header Row: Title + Reset Link */}
        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
          <Grid item>
            <Typography variant="subtitle1" fontWeight="bold" color="gray">
              Product Title
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="body2"
              color="error"
              sx={{ cursor: "pointer", textDecoration: "underline-none" }}
              onClick={() => {
                setProductTitle("")
              }}
            >
              Reset
            </Typography>
          </Grid>
        </Grid>

        {/* Date Inputs */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <FormControl fullWidth>
              <InputLabel id="product-label">Title</InputLabel>
              <Select
                labelId="product-label"
                label="Title"
                value={productTitle}
                onChange={(e) => setProductTitle(e.target.value)}
              >
                <MenuItem value={""}>All Product Title</MenuItem>
                {adminProduct.products?.response?.map((product) => (
                  <MenuItem key={product.id} value={product.title}>
                    {product.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      <Grid container spacing={3} mb={3}>
        {/* Order Date Filters */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, borderRadius: 2, boxShadow: 1, height: '100%' }}>
            <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: "bold", mb: 2 }}>
              Order
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="From"
                type="date"
                variant="outlined"
                size="small"
                value={orderFromDate}
                onChange={(e) => setOrderFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: '180px' } }}
              />
              <TextField
                label="To"
                type="date"
                variant="outlined"
                size="small"
                value={orderToDate}
                onChange={(e) => setOrderToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: '180px' } }}
              />
              <Button
                onClick={handleResetOrderDates}
                sx={{
                  color: "#449AEF",
                  textTransform: "none",
                  fontWeight: 600,
                  '&:hover': { bgcolor: "transparent", textDecoration: "underline" },
                  padding: 0,
                  minWidth: "auto",
                }}
              >
                Reset
              </Button>
            </Box>
          </Card>
        </Grid>

        {/* Payment Date Filters */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, borderRadius: 2, boxShadow: 1, height: '100%' }}>
            <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: "bold", mb: 2 }}>
              Payment
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="From"
                type="date"
                variant="outlined"
                size="small"
                value={paymentFromDate}
                onChange={(e) => setPaymentFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: '180px' } }}
              />
              <TextField
                label="To"
                type="date"
                variant="outlined"
                size="small"
                value={paymentToDate}
                onChange={(e) => setPaymentToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: '180px' } }}
              />
              <Button
                onClick={handleResetPaymentDates}
                sx={{
                  color: "#449AEF",
                  textTransform: "none",
                  fontWeight: 600,
                  '&:hover': { bgcolor: "transparent", textDecoration: "underline" },
                  padding: 0,
                  minWidth: "auto",
                }}
              >
                Reset
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Card className="p-2" sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardHeader
          title={
            <Typography variant="h6" className="text-gray-700">
              PAYMENTS
            </Typography>
          }
        />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, sm: 0 } }}>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExportCSV}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                onClick={handleExportPDF}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Print
              </Button>
            </Box>
            <Box>
              <TextField
                label="Search Payments"
                variant="outlined"
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ width: { xs: '100%', sm: '300px' } }}
              />
            </Box>
          </Box>
          <Box sx={{ height: "100%", width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              pageSizeOptions={[10, 25, 50, 75, 100]}
              getRowId={(row) => row.id}
              checkboxSelection
              onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
              rowSelectionModel={selectedRowIds}
              disableSelectionOnClick
              density="compact"
              experimentalFeatures={{ virtualization: true }}
              loading={payments.loading}
              autoHeight={true}
              sx={{
                borderRadius: 2,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "0.8rem",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "36px !important",
                  minHeight: "36px !important",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog open={openTransferModal} onClose={() => setOpenTransferModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: "center", fontSize: "1.1rem", fontWeight: "bold", mt: 1, mb: 0.5, pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
          Confirm Transfer
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2, textAlign: "center" }}>
          <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
            Are you sure you want to transfer this payment?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "center", gap: 2, p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <Button
            variant="outlined"
            onClick={() => setOpenTransferModal(false)}
            sx={{
              color: "#449AEF",
              borderColor: "#449AEF",
              fontWeight: 600,
              fontSize: "0.85rem",
              borderRadius: "8px",
              px: 3,
              py: 1.2,
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: "#449AEF",
                color: "#fff",
                borderColor: "#449AEF",
                boxShadow: "0 2px 8px rgba(239,68,68,0.1)",
              },
              width: { xs: "100%", sm: "auto" },
              textAlign: "center"
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmTransferAmount}
            sx={{
              background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.85rem",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
              px: 3,
              py: 1.2,
              transition: "all 0.2s",
              "&:hover": {
                background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                transform: "scale(1.02)",
              },
              width: { xs: "100%", sm: "auto" },
              textAlign: "center"
            }}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Payments;
